/**
 * Client tRPC vanilla — connecté à l'API champ-erp.
 *
 * Typage end-to-end automatique : importe le type AppRouter depuis @champ-erp/api
 * (devDependency, type-only). Aucune génération de code requise.
 *
 * Usage typique avec TanStack Query :
 *   const { data } = useQuery({
 *     queryKey: ["health.ping"],
 *     queryFn: () => trpc.health.ping.query(),
 *   });
 */

import type { AppRouter } from "@champ-erp/api/router";
import { createTRPCClient, httpLink, type TRPCClient } from "@trpc/client";

// URL de l'API. En dev, on laisse la chaîne vide pour que le client
// tRPC fasse des fetch relatifs (`/trpc/...`) — Vite proxy redirige vers
// `localhost:3001` en gardant l'origin du front (cf vite.config.ts).
// Cette indirection est requise pour que les cookies SameSite=Strict
// soient posés sur le bon sous-domaine et envoyés à chaque requête.
// En prod, VITE_API_URL reste explicite si l'API est servie ailleurs ;
// par défaut, même origin que le front (architecture URL-first).
const apiUrl = import.meta.env["VITE_API_URL"] ?? "";

/**
 * Lit la valeur d'un cookie côté client. Utilisé pour le double-submit CSRF :
 * le serveur pose `__Host-champ_erp_csrf` (NON-HttpOnly) à la création de
 * session, le client le lit et le renvoie en header `X-CSRF-Token` à
 * chaque mutation.
 *
 * Note : en dev sur localhost (HTTP), le cookie s'appelle `champ_erp_csrf`
 * sans préfixe `__Host-` (qui exige HTTPS).
 */
function readCsrfCookie(): string | null {
  if (typeof document === "undefined") return null;
  const cookies = document.cookie.split(";").map((c) => c.trim());
  for (const cookie of cookies) {
    const eqIdx = cookie.indexOf("=");
    if (eqIdx === -1) continue;
    const name = cookie.slice(0, eqIdx);
    if (name === "__Host-champ_erp_csrf" || name === "champ_erp_csrf") {
      return cookie.slice(eqIdx + 1);
    }
  }
  return null;
}

/**
 * `httpLink` (pas `httpBatchLink`) : on émet 1 requête HTTP par procédure tRPC.
 *
 * Pourquoi pas de batch : quand 2 procédures partent dans la même batch et
 * que l'une renvoie 401 (typiquement `auth.me` sur une page publique comme
 * /invitation/:token, où l'user n'est pas connecté), tRPC v11 + le serveur
 * Hono renvoient HTTP 207 Multi-Status. Le client `httpBatchLink` interprète
 * ce 207 comme une erreur globale et fait échouer TOUTES les procédures de
 * la batch — y compris celles qui ont en fait répondu 200. Conséquence : le
 * `lookupQuery` de la page Invitation passait en `isError` alors que le
 * lookup avait réussi, affichant un faux "Erreur de connexion".
 *
 * Coût en pratique : ~5-10 requêtes HTTP en plus par minute par tab (vs le
 * batch). Négligeable côté infra, et HTTP/2 multiplexe sur la même connexion
 * TCP — pas de surcoût latence perceptible.
 */
export const trpc: TRPCClient<AppRouter> = createTRPCClient<AppRouter>({
  links: [
    httpLink({
      url: `${apiUrl}/trpc`,
      // Pose le header X-CSRF-Token sur chaque mutation (double-submit CSRF,
      // Lot B.2.4). Le serveur compare cookie vs header → 403 si mismatch.
      // Pour les GET (queries), le header n'est pas utile mais le poser ne
      // gêne pas (le middleware skip les méthodes safe).
      headers() {
        const csrf = readCsrfCookie();
        return csrf ? { "X-CSRF-Token": csrf } : {};
      },
      fetch(url, opts) {
        return fetch(url, {
          ...opts,
          // credentials include : envoie le cookie de session HttpOnly (auth Lot B.1)
          credentials: "include",
        });
      },
    }),
  ],
});
